import type { FC, ReactNode } from 'react';
import type React from 'react';
import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';

import { useAppState } from '../../AppStateProvider';
import { useUserQuery } from '../../features/user/useUserQuery';
import { appStore, AppMode } from '../../services/appStore';
import { HttpClient } from '../../services/HttpClient';
import { openLoginWindow } from '../../services/loginService';
import { setSettingsItems } from '../../services/windowMessaging';
import { setDefaultUserSettings } from '../../services/windowMessaging/setDefaultUserSettings';

import { LoginScreen } from './LoginScreen';
import { StyledButton, StyledCheckbox, StyledCloseButton, StyledTermsContainer, StyledTermsModal } from './styles';

const HAS_APPROVED_TERMS_KEY = 'hasApprovedTerms';

export const AuthGuard: FC<{ children: ReactNode }> = (props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const userQuery = useUserQuery();
  const { settingsStorage, mode } = useAppState();
  const [isChecked, setIsChecked] = useState(false);

  const handleClickOnClose = () => {
    window.parent.postMessage({ message: 'close' }, '*');
  };

  const handleApproveTerms = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setSettingsItems({ [HAS_APPROVED_TERMS_KEY]: true });
    appStore.setSettingsStorageValue({ [HAS_APPROVED_TERMS_KEY]: true });
    appStore.setSettingsStorageValue({ isFirstOpen: true });

    setDefaultUserSettings();
  };

  const { refetch } = userQuery;

  useEffect(() => {
    const unregister = HttpClient.registerResponseInterceptor((response) => {
      if (response.status === 401) {
        void refetch();
      }

      return Promise.resolve(response);
    });

    return unregister;
  }, [refetch]);

  const handleLogin = () => {
    openLoginWindow(queryClient);
  };

  if (userQuery.isError) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  if (!userQuery.isFetched) {
    return null;
  }

  if (!settingsStorage.hasApprovedTerms && mode === AppMode.Extension) {
    return (
      <>
        <LoginScreen isTermsPage />
        <StyledTermsContainer>
          <StyledTermsModal data-automation="TermsModal">
            <StyledCloseButton onClick={handleClickOnClose}>
              <SWReactIcons iconName="close" size="12px" />
            </StyledCloseButton>
            <StyledCheckbox
              isChecked={isChecked}
              onClick={() => setIsChecked(!isChecked)}
              label={
                <>
                  {t('terms.agree_and_accept')}&nbsp;
                  <a
                    target="_blank"
                    href="https://www.similarweb.com/corp/legal/sales-intelligence-extension-terms-of-service/"
                  >
                    {t('terms.terms_of_use')}
                  </a>
                </>
              }
              data-automation="TermsCheckbox"
            />
            <StyledButton disabled={!isChecked} onClick={handleApproveTerms} data-automation="TermsButton">
              {t('common.continue')}
            </StyledButton>
          </StyledTermsModal>
        </StyledTermsContainer>
      </>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{props.children}</>;
};
